// src/pages/admin/AdminSecurity.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  alpha,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  TextField,
  MenuItem,
  TablePagination,
  Collapse,
  IconButton,
  Tooltip,
  InputAdornment
} from '@mui/material';
import {
  Security as SecurityIcon,
  Lock as LockIcon,
  Shield as ShieldIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  ShoppingCart as MarketplaceIcon,
  Article as ContentIcon,
  Chat as ChatIcon,
  Engineering as ExpertIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  FilterList as FilterIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import * as adminApi from '../../services/admin';

const AdminSecurity: React.FC = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const [securityLogs, setSecurityLogs] = useState<adminApi.SecurityLog[]>([]);
  const [totalLogs, setTotalLogs] = useState(0);
  const [stats, setStats] = useState({
    totalAdmins: 0,
    activeAdmins: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Activity Log filtering state
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchActivityLogs();
    }, searchQuery ? 500 : 0); // Debounce search by 500ms

    return () => clearTimeout(timer);
  }, [selectedCategory, statusFilter, searchQuery, page, rowsPerPage]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch admin stats
      const admins = await adminApi.listAdmins();

      setStats({
        totalAdmins: admins.length,
        activeAdmins: admins.filter(a => a.isActive).length
      });

      // Fetch activity logs separately
      await fetchActivityLogs();
    } catch (err: any) {
      console.error('Failed to fetch security data:', err);
      setError(err.message || 'Failed to load security data');
    } finally {
      setLoading(false);
    }
  };

  const fetchActivityLogs = async () => {
    try {
      const logsData = await adminApi.getSecurityLogs({
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        category: selectedCategory !== 'all' ? selectedCategory : undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined
      });

      setSecurityLogs(logsData.logs);
      setTotalLogs(logsData.total);
    } catch (err: any) {
      console.error('Failed to fetch activity logs:', err);
      setError(err.message || 'Failed to load activity logs');
    }
  };


  const handleCategoryChange = (_event: React.SyntheticEvent, newValue: string) => {
    setSelectedCategory(newValue);
    setPage(0); // Reset to first page when filter changes
  };

  const handleStatusFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  // Filter logs client-side for search
  const filteredLogs = searchQuery
    ? securityLogs.filter(log => {
        const searchLower = searchQuery.toLowerCase();
        return (
          log.action.toLowerCase().includes(searchLower) ||
          log.category.toLowerCase().includes(searchLower) ||
          log.user?.email?.toLowerCase().includes(searchLower) ||
          log.user?.firstName?.toLowerCase().includes(searchLower) ||
          log.user?.lastName?.toLowerCase().includes(searchLower) ||
          log.actor?.email?.toLowerCase().includes(searchLower) ||
          log.actor?.firstName?.toLowerCase().includes(searchLower) ||
          log.actor?.lastName?.toLowerCase().includes(searchLower) ||
          log.ipAddress?.toLowerCase().includes(searchLower) ||
          JSON.stringify(log.details)?.toLowerCase().includes(searchLower)
        );
      })
    : securityLogs;

  const handlePageChange = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExpandRow = (logId: string) => {
    setExpandedRow(expandedRow === logId ? null : logId);
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'auth': return <LockIcon fontSize="small" />;
      case 'admin': return <AdminIcon fontSize="small" />;
      case 'marketplace': return <MarketplaceIcon fontSize="small" />;
      case 'content': return <ContentIcon fontSize="small" />;
      case 'connection': return <PersonIcon fontSize="small" />;
      case 'chat': return <ChatIcon fontSize="small" />;
      case 'expert_services': return <ExpertIcon fontSize="small" />;
      case 'user': return <PersonIcon fontSize="small" />;
      default: return <SecurityIcon fontSize="small" />;
    }
  };

  const getStatusColor = (status: string): 'success' | 'error' | 'warning' | 'default' => {
    switch (status) {
      case 'success': return 'success';
      case 'failure':
      case 'failed': return 'error';
      case 'error': return 'warning';
      default: return 'default';
    }
  };

  const formatActionName = (action: string) => {
    return action
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <SecurityIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
        <Typography variant="h4" fontWeight={600}>
          Security & Access Control
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Security Overview */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <ShieldIcon sx={{ color: 'success.main', mr: 1 }} />
                <Typography variant="h6">{stats.totalAdmins}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Total Admins
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CheckCircleIcon sx={{ color: 'success.main', mr: 1 }} />
                <Typography variant="h6">{stats.activeAdmins}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Active Admins
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <AccessTimeIcon sx={{ color: 'info.main', mr: 1 }} />
                <Typography variant="h6">{securityLogs.length}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Recent Activities
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <WarningIcon sx={{ color: 'error.main', mr: 1 }} />
                <Typography variant="h6">
                  {securityLogs.filter(l => l.status === 'failed').length}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Failed Attempts
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Security Activity Log */}
      <Paper>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Security Activity Log</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Toggle Filters">
                <IconButton onClick={() => setShowFilters(!showFilters)} size="small">
                  <FilterIcon />
                </IconButton>
              </Tooltip>
              <Button variant="outlined" size="small" onClick={fetchActivityLogs}>
                Refresh
              </Button>
            </Box>
          </Box>

          {/* Category Tabs */}
          <Tabs
            value={selectedCategory}
            onChange={handleCategoryChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mb: showFilters ? 2 : 0 }}
          >
            <Tab label="All" value="all" icon={<SecurityIcon fontSize="small" />} iconPosition="start" />
            <Tab label="Auth" value="auth" icon={<LockIcon fontSize="small" />} iconPosition="start" />
            <Tab label="Admin" value="admin" icon={<AdminIcon fontSize="small" />} iconPosition="start" />
            <Tab label="Marketplace" value="marketplace" icon={<MarketplaceIcon fontSize="small" />} iconPosition="start" />
            <Tab label="Content" value="content" icon={<ContentIcon fontSize="small" />} iconPosition="start" />
            <Tab label="Connection" value="connection" icon={<PersonIcon fontSize="small" />} iconPosition="start" />
            <Tab label="Chat" value="chat" icon={<ChatIcon fontSize="small" />} iconPosition="start" />
            <Tab label="Expert Services" value="expert_services" icon={<ExpertIcon fontSize="small" />} iconPosition="start" />
            <Tab label="User" value="user" icon={<PersonIcon fontSize="small" />} iconPosition="start" />
          </Tabs>

          {/* Search and Additional Filters */}
          <Box sx={{ display: 'flex', gap: 2, pb: 2, flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search by action, user, email, IP..."
              value={searchQuery}
              onChange={handleSearchChange}
              size="small"
              sx={{ flexGrow: 1, minWidth: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                )
              }}
            />
            <Collapse in={showFilters} orientation="horizontal">
              <TextField
                select
                label="Status"
                value={statusFilter}
                onChange={handleStatusFilterChange}
                size="small"
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="success">Success</MenuItem>
                <MenuItem value="failure">Failure</MenuItem>
                <MenuItem value="error">Error</MenuItem>
              </TextField>
            </Collapse>
          </Box>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={40}></TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Actor</TableCell>
                <TableCell>User</TableCell>
                <TableCell>IP Address</TableCell>
                <TableCell>Timestamp</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredLogs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {searchQuery ? 'No activity logs match your search' : 'No activity logs found for the selected filters'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredLogs.map((log) => (
                  <React.Fragment key={log.id}>
                    <TableRow hover sx={{ cursor: 'pointer' }}>
                      <TableCell>
                        <IconButton size="small" onClick={() => handleExpandRow(log.id)}>
                          {expandedRow === log.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getCategoryIcon(log.category)}
                          <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                            {log.category.replace(/_/g, ' ')}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {formatActionName(log.action)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {log.actor ? (
                          <Box>
                            <Typography variant="body2">
                              {log.actor.firstName} {log.actor.lastName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {log.actor.email}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {log.user ? (
                          <Box>
                            <Typography variant="body2">
                              {log.user.firstName} {log.user.lastName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {log.user.email}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {log.ipAddress || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(log.createdAt).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={log.status}
                          color={getStatusColor(log.status)}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                        <Collapse in={expandedRow === log.id} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 2, px: 3, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" gutterBottom>Resource Details</Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  <Typography variant="body2">
                                    <strong>Resource Type:</strong> {log.resourceType || 'N/A'}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>Resource ID:</strong> {log.resourceId || 'N/A'}
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" gutterBottom>Request Details</Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                    <strong>User Agent:</strong> {log.userAgent || 'N/A'}
                                  </Typography>
                                </Box>
                              </Grid>
                              {log.details && Object.keys(log.details).length > 0 && (
                                <Grid item xs={12}>
                                  <Typography variant="subtitle2" gutterBottom>Additional Details</Typography>
                                  <Box
                                    component="pre"
                                    sx={{
                                      p: 1.5,
                                      bgcolor: 'background.paper',
                                      borderRadius: 1,
                                      border: 1,
                                      borderColor: 'divider',
                                      fontSize: '0.75rem',
                                      overflow: 'auto',
                                      maxHeight: 200
                                    }}
                                  >
                                    {JSON.stringify(log.details, null, 2)}
                                  </Box>
                                </Grid>
                              )}
                            </Grid>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalLogs}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </Paper>
    </Box>
  );
};

export default AdminSecurity;
